import { Box, Divider, Button as ECButton } from '@eatclub-apps/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { ReactComponent as IconBYO } from '../../../assets/icons/byo.svg';
import { ReactComponent as IconCake } from '../../../assets/icons/cake.svg';
import { ReactComponent as IconChildFriendlyVenue } from '../../../assets/icons/child_friendly_venue.svg';
import { ReactComponent as IconCorkage } from '../../../assets/icons/corkage.svg';
import { ReactComponent as IconDate } from '../../../assets/icons/date.svg';
import { ReactComponent as IconDisabledAccess } from '../../../assets/icons/disabled_access.svg';
import { ReactComponent as IconDressCode } from '../../../assets/icons/dress_code.svg';
import { ReactComponent as IconGuests } from '../../../assets/icons/guests.svg';
import { ReactComponent as IconMarker } from '../../../assets/icons/marker.svg';
import { ReactComponent as IconChildrensPlayArea } from '../../../assets/icons/playground.svg';
import { ReactComponent as IconTime } from '../../../assets/icons/time.svg';
// import { AddGuestsToBooking } from '../../../components/AddGuestsToBooking';
import { Button } from '../../../components/Button';
import { DietariesSummary } from '../../../components/DietariesSummary';
import { Information } from '../../../components/Information';
import { PaymentSummary } from '../../../components/PaymentSummary';
import { Spacer } from '../../../components/Spacer';
import { Typography } from '../../../components/Typography';
import {
  batchUpdateBookingAction,
  clearBookingAction,
  editBookingAction,
  editWaitlistAction,
} from '../../../data/actions/bookingAction';
import { bookingKeys, bookingPropTypes, isEditing } from '../../../data/models/Booking';
import { restaurantPropTypes } from '../../../data/models/Restaurant';
import {
  STATUS_TYPES,
  allowedStatusesForCancel,
  getConfirmationContent,
  getConfirmationIcon,
  getConfirmationTitle,
  isPastBooking,
} from '../../../data/models/obeeBooking';
import { ALL_SESSIONS_LABEL } from '../../../data/models/obeeSessions';
import { PageLayout } from '../../../layouts/PageLayout';
import { WidgetLayout } from '../../../layouts/WidgetLayout';
import { trackEvent } from '../../../utils/analytics';
import { bookingCanBeEdited } from '../../../utils/bookingHelpers';
import {
  byoMessage,
  cakesMessage,
  childrensMenuMessage,
  corkageMessage,
  disabledAccessMessage,
  dressCodeMessage,
  playAreaMessage,
} from '../../../utils/constants';
import {
  formatDateToMoment,
  formatDateWithYear,
  formatIntFromTime,
  formatTimeStringToTimeString,
  getDateFormatWithConditionalYear,
  isEmpty,
  minutesToTimeString,
  timeStringToTime,
} from '../../../utils/helpers';
import { useIsMobile } from '../../../utils/hooks';
import { useCoordinates } from '../../../utils/hooks/useCoordinates';
import { CancelBookingModal } from './CancelBookingModal';
import useStyles from './ConfirmStyles';
// import { GoogleCalendar } from './GoogleCalendar';
import { Map } from './Map';

// import { ReviewExperienceModal } from './ReviewExperienceModal';

const Confirm = ({
  batchUpdateBooking,
  booking,
  clearBooking,
  confirmCancel,
  editBooking,
  editWaitlist,
  obeeBooking,
  restaurant,
  action,
}) => {
  const theme = useTheme();

  const [searchParams] = useSearchParams();
  const bCode = searchParams.get('code');

  const getCheckinDetails = () => {
    const allAddonGroups = (booking?.selectedExperiences || []).flatMap(
      (experience) => experience?.experience?.addonGroups,
    );

    return allAddonGroups.find((addonGroup) => addonGroup?.checkinDetails?.checkinTime)
      ?.checkinDetails;
  };

  const checkInDetails = getCheckinDetails();
  const checkInTime = !isEmpty(checkInDetails?.checkinTime)
    ? formatIntFromTime(timeStringToTime(checkInDetails?.checkinTime, 'HH:mm:ss'))
    : null;

  const classes = useStyles({ hasCheckinTime: !isEmpty(checkInTime) });
  // const [openReviewExperienceModal, setOpenReviewExperienceModal] = useState(false);
  // const [hasReviewed, setHasReviewed] = useState(false);
  const isMobile = useIsMobile();

  const { longitude, latitude, address } = restaurant.data;
  const coordinates = useCoordinates(longitude, latitude, address);

  const pastBooking = isPastBooking(obeeBooking.data);

  const isWaitlist = obeeBooking.data.status === STATUS_TYPES.WAITLIST && !pastBooking;

  const isBooked =
    [STATUS_TYPES.CONFIRMED, STATUS_TYPES.BOOKED].includes(obeeBooking.data.status) && !pastBooking;

  const isConfirmed =
    [STATUS_TYPES.CONFIRMED, STATUS_TYPES.ARRIVED].includes(obeeBooking.data.status) &&
    !pastBooking;

  const isCancelled =
    [STATUS_TYPES.NO_SHOW, STATUS_TYPES.CANCELLED, STATUS_TYPES.UNCONFIRMED].includes(
      obeeBooking.data.status,
    ) && !pastBooking;

  const isCancelledWaitlist = obeeBooking.data.status === STATUS_TYPES.UNCONFIRMED && !pastBooking;

  const isFinished = obeeBooking.data.status === STATUS_TYPES.FINISHED || pastBooking;

  const [openCancelBookingModal, setOpenCancelBookingModal] = useState(
    allowedStatusesForCancel.includes(obeeBooking.data.status) && !pastBooking && confirmCancel,
  );

  const dateFormat = getDateFormatWithConditionalYear(
    obeeBooking.data.date,
    'h:mma on dddd Do MMMM',
  );

  const getConfirmationCard = () => {
    if (isFinished) {
      return null;
    }

    return (
      <Spacer direction='vertical' gap={32} style={{ width: '100%' }}>
        <Box className={classes.details}>
          <Spacer gap='s' className={classes.item}>
            <IconDate fill={restaurant.data.iconBgColour} />
            <Typography>{formatDateWithYear(obeeBooking.data.date, 'ddd Do MMM')}</Typography>
          </Spacer>
          <Spacer gap='s' className={classes.item}>
            <IconGuests fill={restaurant.data.iconBgColour} />
            <Typography>
              {`${obeeBooking.data.size} ${obeeBooking.data.size > 1 ? 'guests' : 'guest'}`}
            </Typography>
          </Spacer>
          <Spacer gap='s' className={classes.item}>
            <IconTime fill={restaurant.data.iconBgColour} />
            <Typography>{`${
              isWaitlist || isCancelledWaitlist
                ? 'Requested time'
                : restaurant?.data?.arrivalTimeLabel
            } ${minutesToTimeString(obeeBooking.data.time)}`}</Typography>
          </Spacer>

          {!isEmpty(checkInTime) && (
            <Spacer gap='s' className={classes.item}>
              <IconTime fill={restaurant.data.iconBgColour} />
              <Typography>{`Check-in ${minutesToTimeString(checkInTime)}`}</Typography>
            </Spacer>
          )}
        </Box>

        {!isEmpty(restaurant.data?.confirmationImageUrl) && (
          <>
            <Box
              style={{
                maxWidth: '640px',
                maxHeight: '380px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={restaurant.data.confirmationImageUrl}
                style={{ maxWidth: '640px', maxHeight: '380px' }}
                alt={`${restaurant.data.name} booking confirmation`}
              />
            </Box>
            <Divider color='black' />
          </>
        )}

        {/* todo: Hide feature for now till google approved */}
        {/* {(isBooked || isConfirmed) && <GoogleCalendar />} */}

        <Spacer direction='vertical' gap={24}>
          <Spacer direction='vertical' gap='s'>
            <Typography variant='h4'>{obeeBooking.data.name}</Typography>

            <Typography>
              {obeeBooking.data.guests?.[0].email}
              {!isEmpty(obeeBooking.data.guests?.[0].email) &&
                !isEmpty(obeeBooking.data.guests?.[0].mobile) && (
                  <span style={{ color: theme.colors.outlineColour, padding: '0 6px' }}>
                    {' | '}
                  </span>
                )}
              {obeeBooking.data.guests?.[0].mobile}
            </Typography>

            {obeeBooking.data.kids > 0 && (
              <>
                <Typography style={{ color: theme.colors.secondaryTextColour }}>
                  Children
                </Typography>
                <Typography style={{ overflowWrap: 'anywhere' }}>
                  {obeeBooking.data.kids}
                </Typography>
              </>
            )}

            {obeeBooking.data.highchairs > 0 && (
              <>
                <Typography style={{ color: theme.colors.secondaryTextColour }}>
                  High chairs
                </Typography>
                <Typography style={{ overflowWrap: 'anywhere' }}>
                  {obeeBooking.data.highchairs}
                </Typography>
              </>
            )}
          </Spacer>

          {!isEmpty(obeeBooking.data.customerComments) && (
            <>
              <Divider color={theme.colors.dividerColor} />
              <Spacer direction='vertical' gap='m'>
                <Typography variant='h4'>Special requests</Typography>
                <Typography>{obeeBooking.data.customerComments}</Typography>
              </Spacer>
            </>
          )}

          {/* Hide PaymentSummary when click link from confirmation email */}
          {!bCode && <PaymentSummary />}

          {/* Actions */}
          {(isBooked || isConfirmed || isWaitlist) && (
            <>
              <Divider color={theme.colors.dividerColor} />
              <Spacer>
                {bookingCanBeEdited(obeeBooking, action) && (
                  <Button
                    size='small'
                    onClick={() => {
                      trackEvent('Booking', 'Editing Booking', 'Confirmation');

                      if (booking.initialRestId !== restaurant.data.objectId) {
                        // reset some information that might clash when editing a multi venue booking
                        batchUpdateBooking({
                          [bookingKeys.areaId]: -1,
                          [bookingKeys.sessionId]: -1,
                          [bookingKeys.sessionLabel]: ALL_SESSIONS_LABEL,
                          [bookingKeys.preferredTime]: -1,
                        });
                      }

                      editBooking();
                    }}
                    variant='outlined'
                    style={{ fontWeight: 'normal', padding: '3px 16px' }}
                  >
                    Edit booking
                  </Button>
                )}

                {!isWaitlist && (
                  <Button
                    size='small'
                    onClick={() => {
                      trackEvent('Booking', 'Add to calendar', 'Confirmation');

                      // Get start and end times
                      const momentDateTime = formatDateToMoment(
                        `${obeeBooking.data.date} ${minutesToTimeString(obeeBooking.data.time)}`,
                        'YYYY-MM-DD h:mma',
                      );

                      // Some weird time issue with google calendar, seems like it cannot handle YYYYMMDDTHH:mm:ssZ properly
                      // So we need to use YYYYMMDDTHHmmssZ format instead for the start and end times
                      const startTimeForGoogle = momentDateTime.format('YYYYMMDDTHHmmssZ');
                      const endTimeForGoogle = momentDateTime
                        .clone()
                        .add(obeeBooking.data.duration, 'minutes')
                        .format('YYYYMMDDTHHmmssZ');

                      // Create the parameters for the Google Calendar event
                      const params = {
                        action: 'TEMPLATE',
                        text: `${restaurant.data.name} Reservation`,
                        dates: `${startTimeForGoogle}/${endTimeForGoogle}`,
                        details: '',
                        location: restaurant.data.address,
                        // Pass the restaurant timezone to google calendar
                        ctz: restaurant.data.timezone,
                      };

                      const encodedParams = Object.entries(params)
                        .map((keyValue) => keyValue.map(encodeURIComponent).join('='))
                        .join('&');

                      // Open a new window in google calendar to save this event
                      window.open(`http://www.google.com/calendar/event?${encodedParams}`);
                    }}
                    variant='outlined'
                    style={{ fontWeight: 'normal', padding: '3px 16px' }}
                  >
                    Add to calendar
                  </Button>
                )}
              </Spacer>
            </>
          )}
        </Spacer>

        {!isEmpty(checkInDetails) && (
          <Spacer direction='vertical' gap={32}>
            <Divider color='black' />
            <Typography variant='h3'>Your stay</Typography>
            <Spacer gap={32} direction='vertical'>
              <Spacer>
                <Box className={classes.checkinTimeLabel}>Check in</Box>
                <Box className={classes.checkinTimeValue}>
                  {/* Add the time to the day the booking is made for TODO clean this */}
                  {formatTimeStringToTimeString(
                    `${checkInDetails?.checkinTime} ${formatDateToMoment(
                      obeeBooking.data.date,
                    ).format('dddd Do MMMM YYYY')}`,
                    'HH:mm:ss dddd Do MMMM YYYY',
                    dateFormat,
                  )}
                </Box>
              </Spacer>

              {!isEmpty(checkInDetails?.checkoutTime) && checkInDetails?.showCheckoutTime && (
                <>
                  <Divider color={theme.colors.dividerColor} />

                  <Spacer>
                    <Box className={classes.checkinTimeLabel}>Check out</Box>
                    {/* TODO this is a hardcoded 1 day checkout */}
                    <Box className={classes.checkinTimeValue}>
                      {timeStringToTime(
                        `${checkInDetails?.checkoutTime} ${formatDateToMoment(
                          obeeBooking.data.date,
                        ).format('dddd Do MMMM')}`,
                        'HH:mm:ss dddd Do MMMM',
                      )
                        .add(1, 'day')
                        .format('h:mma on dddd Do MMMM')}
                    </Box>
                  </Spacer>
                </>
              )}
            </Spacer>
          </Spacer>
        )}

        {restaurant?.data?.showDietaryRequirements && !isEmpty(booking.dietaryRequirements) && (
          <>
            <Divider color='black' />
            <DietariesSummary />
            {/* <Divider color='black' /> */}
          </>
        )}

        {/* /!* TODO this is not a feature we support yet *!/ */}
        {/* <AddGuestsToBooking /> */}
      </Spacer>
    );
  };

  return (
    <PageLayout>
      <WidgetLayout>
        <Spacer direction='vertical' gap={isMobile ? 'm' : 'l'} centered>
          <Spacer direction='vertical' gap='m' centered>
            {getConfirmationIcon(isWaitlist, restaurant.data.premium, theme)}

            <Typography variant='h2' centered style={{ maxWidth: '280px' }}>
              {getConfirmationTitle(
                pastBooking,
                obeeBooking.data.status,
                isEditing(booking),
                restaurant,
              )}
            </Typography>

            <Typography centered style={{ maxWidth: '400px' }}>
              {getConfirmationContent(
                pastBooking,
                obeeBooking.data.status,
                isEditing(booking),
                restaurant,
              )}
            </Typography>
          </Spacer>

          {getConfirmationCard()}

          {/* todo: Hide feature for now till apis are done */}
          {/* {(isBooked || isConfirmed) &&
            (hasReviewed ? (
              <Typography variant='h4'>Thank you for your feedback!</Typography>
            ) : (
              <Button
                onClick={() => {
                  trackEvent('Booking', 'Viewed review experience modal');
                  setOpenReviewExperienceModal(true);
                }}
                size='large'
                color='primary'
              >
                Review experience
              </Button>
            ))} */}

          {(pastBooking || isCancelled || isCancelledWaitlist || isFinished) && (
            <>
              <Divider color='black' />
              <Spacer direction='vertical' gap='m' centered>
                <Typography variant='h4'>Need to make a new booking?</Typography>
                <Button
                  onClick={() => {
                    trackEvent('Booking', 'Adding New Booking');
                    clearBooking();
                  }}
                  size='large'
                  color='primary'
                >
                  Start new booking
                </Button>
              </Spacer>
            </>
          )}
        </Spacer>
      </WidgetLayout>

      {restaurant.data?.displayHelpfulInfo && (
        <>
          <Divider color='black' />
          <WidgetLayout>
            <Spacer direction='vertical' gap='l'>
              <Typography variant='h4'>Helpful information</Typography>

              <Spacer direction='vertical' gap='m'>
                <Information
                  Icon={<IconDisabledAccess />}
                  heading='Disabled access'
                  description={disabledAccessMessage?.[restaurant.data?.disabledOption]}
                  show={restaurant.data?.hasDisabledAccess && restaurant.data?.disabledOption}
                />

                <Information
                  Icon={<IconDressCode />}
                  heading='Dress code'
                  description={dressCodeMessage?.[restaurant.data?.dressCodeOption]}
                  show={restaurant.data?.hasDressCode && restaurant.data?.dressCodeOption}
                />

                <Information
                  Icon={<IconChildFriendlyVenue />}
                  heading='Child friendly venue'
                  description={childrensMenuMessage?.[restaurant.data?.kidsMenuOption]}
                  show={restaurant.data?.hasKidsMenu && restaurant.data?.kidsMenuOption}
                />

                <Information
                  Icon={<IconChildrensPlayArea />}
                  heading="Children's play area"
                  description={playAreaMessage?.[restaurant.data?.playAreaOption]}
                  show={restaurant.data?.hasPlayArea && restaurant.data?.playAreaOption}
                />

                <Information
                  Icon={<IconBYO />}
                  heading='BYO'
                  description={byoMessage(restaurant.data?.byoOption)}
                  show={restaurant.data?.hasBYO && restaurant.data?.byoOption}
                />

                <Information
                  Icon={<IconCorkage />}
                  heading='Corkage'
                  description={corkageMessage(
                    restaurant.data?.corkageOption,
                    restaurant.data?.corkageFee,
                    restaurant.data?.corkagePer,
                  )}
                  show={restaurant.data?.hasCorkage && restaurant.data?.corkageOption}
                />

                <Information
                  Icon={<IconCake />}
                  heading='Cake'
                  description={cakesMessage?.[restaurant.data?.cakesOption]}
                  show={restaurant.data?.hasCakes && restaurant.data?.cakesOption}
                />
              </Spacer>
            </Spacer>
          </WidgetLayout>
        </>
      )}

      {address && (isWaitlist || isBooked || isConfirmed) && (
        <>
          <Divider color='black' />
          <WidgetLayout>
            <Spacer direction='vertical' gap={isMobile ? 'm' : 'l'}>
              <Spacer style={{ justifyContent: 'space-between' }} wrap>
                <Typography variant='h4'>How to get there</Typography>
                <Button
                  href={`http://maps.google.com?q=${address}`}
                  target='_blank'
                  variant='text'
                  style={{ fontWeight: theme.fontWeights.regular }}
                >
                  <Spacer>
                    <IconMarker />

                    <Typography>{address}</Typography>
                  </Spacer>
                </Button>
              </Spacer>
              {coordinates && <Map coordinates={coordinates} />}
            </Spacer>
          </WidgetLayout>
        </>
      )}

      {allowedStatusesForCancel.includes(obeeBooking.data.status) && !pastBooking && (
        <CancelBookingModal
          open={openCancelBookingModal}
          close={() => setOpenCancelBookingModal(false)}
        />
      )}

      {/* TODO: backend required */}
      {/* <ReviewExperienceModal
        open={openReviewExperienceModal}
        close={() => setOpenReviewExperienceModal(false)}
      /> */}
    </PageLayout>
  );
};

Confirm.defaultProps = {
  confirmCancel: false,
};

Confirm.propTypes = {
  batchUpdateBooking: PropTypes.func.isRequired,
  booking: bookingPropTypes.isRequired,
  clearBooking: PropTypes.func.isRequired,
  confirmCancel: PropTypes.bool,
  editBooking: PropTypes.func.isRequired,
  editWaitlist: PropTypes.func.isRequired,
  obeeBooking: bookingPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  action: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  restaurant: state.restaurant,
  obeeBooking: state.obeeBooking,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      batchUpdateBooking: batchUpdateBookingAction,
      clearBooking: clearBookingAction,
      editBooking: editBookingAction,
      editWaitlist: editWaitlistAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Confirm);
